import { useState } from 'react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useLyricStore } from '../../../../stores/lyricStore'
import { useSettingsStore } from '../../../../stores/settingsStore'
import type { LyricInput, LyricResult } from '@creanizer/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { AlertCircle, Loader2, RefreshCw, Save, Check } from 'lucide-react'
import { LyricForm } from './LyricForm'
import { LyricResult as LyricResultComponent } from './LyricResult'
import { SavedLyricsList } from './SavedLyricsList'
import { 
  analyzeError, 
  getErrorMessage,
  logGenerationError 
} from '../../utils/errorHandling'

export function LyricGenerator() {
  const [input, setInput] = useState<LyricInput>({
    // デフォルトはすべて自動
    mode: 'quick',
    genre: 'auto',
    language: 'auto',
    structure: 'auto',
    creativityLevel: 'auto'
  })
  
  const [inputMode, setInputMode] = useState<'quick' | 'detailed'>('quick')
  const [activeTab, setActiveTab] = useState<'generate' | 'saved'>('generate')
  const [useStreaming, setUseStreaming] = useState(true) // デフォルトでストリーミングON
  const [streamingResult, setStreamingResult] = useState<LyricResult | null>(null)
  const [streamingError, setStreamingError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<{ title: string; message: string; action?: string } | null>(null)
  const [isSaved, setIsSaved] = useState(false) // 保存済み状態を追跡
  
  const generator = useGenerator('lyric')
  const streamGenerator = useStreamGenerator('lyric')
  const lyricStore = useLyricStore()
  const settingsStore = useSettingsStore()
  
  const handleGenerate = async () => {
    setErrorDetails(null)
    setIsSaved(false) // 新しい生成時は未保存状態にリセット
    
    if (useStreaming) {
      // ストリーミングモード
      setStreamingResult(null)
      setStreamingError(null)
      
      try {
        await new Promise<void>((resolve, reject) => {
          streamGenerator.generateStream({ ...input, mode: inputMode }, {
            onChunk: (chunk) => {
              console.log('[Lyric Stream] Received chunk:', chunk.length)
            },
            onProgress: (accumulated) => {
              console.log('[Lyric Stream] Progress:', accumulated)
            },
            onComplete: (result: LyricResult) => {
              console.log('[Lyric Stream] Complete:', result)
              setStreamingResult(result)
              resolve()
            },
            onError: async (error) => {
              logGenerationError('Lyric', error, { input, mode: 'streaming' })
              reject(error)
            }
          })
        })
      } catch (error) {
        const errorType = analyzeError(error)
        const errorMsg = getErrorMessage(errorType, error?.message)
        setErrorDetails(errorMsg)
        setStreamingError(errorMsg.message)
      }
    } else {
      // 通常モード
      try {
        const response = await generator.mutateAsync({ ...input, mode: inputMode })
        // 結果を表示のみ（保存はしない）
      } catch (error) {
        logGenerationError('Lyric', error, { input, mode: 'normal' })
        
        const errorType = analyzeError(error)
        const errorMsg = getErrorMessage(errorType, error?.message)
        setErrorDetails(errorMsg)
      }
    }
  }
  
  // 手動保存関数
  const handleSave = () => {
    const result = streamingResult || generator.data?.result
    if (result) {
      lyricStore.addLyric({
        input,
        result: result as LyricResult
      })
      setIsSaved(true)
    }
  }
  
  // 保存済みデータから設定をロード
  const handleLoadInput = (loadedInput: LyricInput) => {
    setInput(loadedInput)
    // 詳細モードに切り替え（設定が多い場合）
    if (loadedInput.mode === 'detailed' || 
        (loadedInput.theme || loadedInput.characterIds || loadedInput.worldContext)) {
      setInputMode('detailed')
    } else {
      setInputMode('quick')
    }
    // 生成タブに切り替え
    setActiveTab('generate')
  }
  
  // 保存済みデータから再生成
  const handleRegenerate = (loadedInput: LyricInput) => {
    setInput(loadedInput)
    if (loadedInput.mode === 'detailed' || 
        (loadedInput.theme || loadedInput.characterIds || loadedInput.worldContext)) {
      setInputMode('detailed')
    } else {
      setInputMode('quick')
    }
    setActiveTab('generate')
    // 少し遅延してから生成開始
    setTimeout(() => {
      handleGenerate()
    }, 100)
  }
  
  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
        <span className="text-4xl">🎵</span>
        リリックジェネレータ
      </h1>
      
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'generate' | 'saved')}>
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="generate">歌詞を生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({lyricStore.lyrics.length}/200)
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-6">
          {/* モード設定 */}
          <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
            {/* Proモード */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="pro-mode"
                checked={input.useProMode || false}
                onCheckedChange={(checked) => setInput({ ...input, useProMode: checked as boolean })}
                disabled={generator.isPending || streamGenerator.isStreaming}
              />
              <Label htmlFor="pro-mode" className="cursor-pointer">
                Proモード
                <span className="text-sm text-gray-500 ml-2">
                  （より高度なAIモデルを使用）
                </span>
              </Label>
            </div>
            
            {/* ストリーミングモード */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="streaming-mode"
                checked={useStreaming}
                onCheckedChange={(checked) => setUseStreaming(checked as boolean)}
                disabled={generator.isPending || streamGenerator.isStreaming}
              />
              <Label htmlFor="streaming-mode" className="cursor-pointer">
                ストリーミングモード
                <span className="text-sm text-gray-500 ml-2">
                  （リアルタイムで生成過程を表示）
                </span>
              </Label>
            </div>
          </div>
          
          {/* 入力フォーム */}
          <LyricForm
            input={input}
            onChange={setInput}
            onGenerate={handleGenerate}
            isGenerating={generator.isPending || streamGenerator.isStreaming}
            inputMode={inputMode}
            onInputModeChange={setInputMode}
          />
          
          {/* ストリーミング中のプログレス表示 */}
          {streamGenerator.isStreaming && (
            <div className="space-y-4 p-6 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="font-medium text-blue-900">
                  歌詞を生成中...
                </span>
              </div>
              <Progress value={streamGenerator.progress} className="h-2" />
              <p className="text-sm text-blue-700">
                進捗: {Math.round(streamGenerator.progress)}%
              </p>
              
              {/* 部分的な内容のプレビュー */}
              {streamGenerator.partialContent && (
                <div className="mt-4 p-4 bg-white rounded border border-blue-200">
                  <p className="text-sm text-gray-600 mb-2">生成中のコンテンツ:</p>
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-auto max-h-40">
                    {streamGenerator.partialContent.slice(-500)}
                  </pre>
                </div>
              )}
            </div>
          )}
          
          {/* エラー表示（詳細版） */}
          {errorDetails && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{errorDetails.title}</AlertTitle>
              <AlertDescription className="mt-2 space-y-2">
                <p>{errorDetails.message}</p>
                {errorDetails.action && (
                  <p className="text-sm font-medium">{errorDetails.action}</p>
                )}
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGenerate}
                    disabled={generator.isPending || streamGenerator.isStreaming}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    再試行
                  </Button>
                  {errorDetails.title === 'APIキーエラー' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.location.href = '/settings'}
                    >
                      設定を開く
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          {/* 旧エラー表示（フォールバック） */}
          {!errorDetails && (generator.isError || streamingError) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {streamingError || generator.error?.message || '歌詞の生成に失敗しました'}
              </AlertDescription>
            </Alert>
          )}
          
          {/* 結果表示 */}
          {(generator.data || streamingResult) && (
            <div className="space-y-4">
              {/* 保存ボタン */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={isSaved}
                  variant={isSaved ? "outline" : "default"}
                  className="min-w-[120px]"
                >
                  {isSaved ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      保存済み
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      保存する
                    </>
                  )}
                </Button>
              </div>
              
              {/* 結果コンポーネント */}
              <LyricResultComponent 
                result={(streamingResult || generator.data?.result) as LyricResult} 
              />
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="saved">
          <SavedLyricsList 
            onLoadInput={handleLoadInput}
            onRegenerate={handleRegenerate}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}